// src/components/tasks/MyTasksCard.tsx
// Personal tasks card with inline quick add
import React, { useState, useMemo, useRef, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { add } from 'ionicons/icons';
import { Card } from '../ui/Card';
import { TaskItem } from './TaskItem';
import type { TaskAssignment, FamilyMember } from '../../types';
import './tasks.css';

export interface MyTasksCardProps {
  assignments: TaskAssignment[];
  currentMember: FamilyMember | null;
  onToggle: (assignmentId: string, completed: boolean, event: React.MouseEvent) => void;
  onQuickAdd: (taskName: string) => Promise<void> | void;
  onTaskClick?: (assignment: TaskAssignment) => void;
  showCompleted?: boolean;
}

export const MyTasksCard: React.FC<MyTasksCardProps> = ({
  assignments,
  currentMember,
  onToggle,
  onQuickAdd,
  onTaskClick,
  showCompleted = false,
}) => {
  const [quickAddValue, setQuickAddValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter to current member's tasks
  const myTasks = useMemo(() => {
    if (!currentMember) return [];

    let filtered = assignments.filter(a => a.assigned_to === currentMember.id);

    if (!showCompleted) {
      filtered = filtered.filter(a => !a.is_completed);
    }

    // Sort: incomplete first, then by due date
    filtered.sort((a, b) => {
      if (a.is_completed !== b.is_completed) {
        return a.is_completed ? 1 : -1;
      }
      return (a.due_date || '').localeCompare(b.due_date || '');
    });

    return filtered;
  }, [assignments, currentMember, showCompleted]);

  const totalRemaining = assignments.filter(
    a => a.assigned_to === currentMember?.id && !a.is_completed
  ).length;

  // Handle quick add submission
  const handleQuickAddSubmit = useCallback(async () => {
    const trimmedValue = quickAddValue.trim();
    if (!trimmedValue || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onQuickAdd(trimmedValue);
      setQuickAddValue('');
    } finally {
      setIsSubmitting(false);
      // Keep focus on input for adding more tasks
      inputRef.current?.focus();
    }
  }, [quickAddValue, isSubmitting, onQuickAdd]);

  // Handle key press in input
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuickAddSubmit();
    } else if (e.key === 'Escape') {
      setQuickAddValue('');
      inputRef.current?.blur();
    }
  }, [handleQuickAddSubmit]);

  // Handle task item click
  const handleTaskClick = useCallback((assignment: TaskAssignment) => {
    if (onTaskClick) {
      onTaskClick(assignment);
    }
  }, [onTaskClick]);

  return (
    <Card
      title="My Tasks"
      subtitle={currentMember ? `${totalRemaining} remaining` : undefined}
      size="md"
    >
      <div className="my-tasks-card">
        {!currentMember ? (
          <div className="my-tasks-card__empty">
            <span className="my-tasks-card__empty-icon">👤</span>
            <p>Link your account to a family member to see your tasks</p>
          </div>
        ) : myTasks.length === 0 ? (
          <div className="my-tasks-card__empty">
            <span className="my-tasks-card__empty-icon">✨</span>
            <p>All caught up! No tasks assigned.</p>
          </div>
        ) : (
          <div className="my-tasks-card__list">
            {myTasks.map(assignment => (
              <TaskItem
                key={assignment.id}
                assignment={assignment}
                onToggle={onToggle}
                onClick={() => handleTaskClick(assignment)}
                showDueDate
                showPoints
                compact
              />
            ))}
          </div>
        )}

        {/* Inline Quick Add Input */}
        <div className="my-tasks-card__quick-add-container">
          <IonIcon icon={add} className="my-tasks-card__quick-add-icon" />
          <input
            ref={inputRef}
            type="text"
            className="my-tasks-card__quick-add-input"
            placeholder="Add a task..."
            value={quickAddValue}
            onChange={(e) => setQuickAddValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting || !currentMember}
          />
          <button
            className="my-tasks-card__quick-add-submit"
            onClick={handleQuickAddSubmit}
            disabled={isSubmitting || !quickAddValue.trim()}
            aria-label="Add task"
          >
            <IonIcon icon={add} />
          </button>
        </div>
      </div>
    </Card>
  );
};

MyTasksCard.displayName = 'MyTasksCard';

export default MyTasksCard;
